import { useHttp } from "@/application/HttpClient";
import { Laundry } from "@/models/Laundry";
import { laundryService } from "@/services/laundry";
import { compareByName } from "@/utilities/collections/compare";
import { useCollection } from "@/utilities/collections/useCollection";
import { Select } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LaundrySelectInputProps, OptionType } from "./types";

type ComponentType = React.FC<LaundrySelectInputProps>;
export const LaundrySelectInput: ComponentType = ({ onChange, ...props }) => {
	const { request } = useHttp();

	const [loading, setLoading] = useState<boolean>(true);
	const [laundries, setLaundries] = useCollection<Laundry>([], compareByName);

	const childValue = useMemo(
		() =>
			!loading
				? typeof props.value !== "string"
					? props.value?.id
					: props.value
				: undefined,
		[loading, props.value]
	);

	const options = useMemo<OptionType[]>(
		() => laundries.map((payload) => ({ value: payload.id, label: payload.name, payload })),
		[laundries]
	);

	const onFilter = useCallback((value: string, option?: OptionType) => {
		const reference = option?.label.toLowerCase();
		const test = value.toLowerCase();

		return reference?.includes(test) || false;
	}, []);

	const onChildChange = useCallback(
		(option?: OptionType) => onChange?.(option?.payload),
		[onChange]
	);

	useEffect(() => {
		if (props.customerId) {
			setLoading(true);
			request(laundryService.list, {
				sortOrder: "",
				sortField: "",
				filterField: props.customerId ? "customerId" : "",
				filterText: props.customerId ? props.customerId : "",
			})
				.then((laundries) => setLaundries(laundries))
				.catch(() => setLaundries([]))
				.finally(() => setLoading(false));
		} else {
			setLoading(false)
		}
	}, [request, setLaundries, props.customerId]);

	return (
		<Select
			allowClear
			showSearch
			value={childValue}
			disabled={props.disabled}
			loading={loading}
			options={options}
			filterOption={onFilter}
			onChange={(_value, option) => onChildChange(option as OptionType)}
		/>
	);
};
